import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { m } from "@recobra/i18n/messages";
import type { Task } from "@recobra/schemas/task";

import { getErrorMessage, tasksApi } from "../../lib/api";

/**
 * Emails the task's assignee. Not optimistic: the server decides whether the
 * email is sent, skipped or refused, so the caller shows the send as in progress
 * while pending and the server's record is then added to the cached task.
 */
export const useEmailTaskAssignee = (task: Task) => {
  const queryClient = useQueryClient();

  return useMutation({
    scope: { id: `task-emails-${task.id}` },
    meta: { suppressErrorToast: true },
    mutationFn: () => tasksApi.emailAssignee(task.id),
    onSuccess: (email) => {
      queryClient.setQueryData<Task>(["tasks", task.id], (cached) => {
        const alreadyRefetched = cached?.emails.some(({ id }) => id === email.id);
        if (!cached || alreadyRefetched) return cached;
        return { ...cached, emails: [...cached.emails, email] };
      });
    },
    onError: (error) => {
      toast.error(m.task_email_send_error({ message: getErrorMessage(error) }));
    },
  });
};
